import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';

import { DollarSign, Pencil, Trash2 } from 'lucide-react';

import { ConfirmDialog } from '@/ui/ConfirmDialog';
import { DataTable, type DataTableColumn } from '@/ui/DataTable';
import { DateRangePresetFilter } from '@/ui/DateRangePresetFilter';
import { resolveDateRange, type DateRangeValue } from '@/ui/dateRangePreset';
import { FilterBar, type FilterFieldConfig } from '@/ui/FilterBar';
import { MoneyDisplay } from '@/ui/MoneyDisplay';
import { MoneyInput } from '@/ui/MoneyInput';
import { ResponsiveModal } from '@/ui/ResponsiveModal';
import { Badge } from '@/ui/base/badge';
import { Button } from '@/ui/base/button';
import { Input } from '@/ui/base/input';
import { Label } from '@/ui/base/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/base/select';
import { Switch } from '@/ui/base/switch';
import { Textarea } from '@/ui/base/textarea';

import { useCenarioDeTela } from '../_dev/cenarioDeTela';
import { EstadoSimulado } from '../_dev/EstadoSimulado';
import DADOS_SIMULADOS from './dados-simulados.json';
import {
  TIPO_PESSOA_LABELS,
  formatarPercentual,
  gerarIdLocal,
  type ComponenteRemuneracao,
  type DadosTabelaDePrecos,
  type TipoPessoa,
} from './tipos';
import { TextoVigencia } from './VigenciaBadge';

/**
 * Aba "Componentes da Remuneração": versões, por vigência, dos valores que
 * compõem a remuneração mensal do transportador — per capita, fixo mensal,
 * cadeirante, creche, especial e KM adicional — por tipo de pessoa (PF, PJ,
 * Cooperativa).
 *
 * Dado simulado por decisão de produto (ver `../_dev/cenarioDeTela.ts`): a
 * API do módulo financeiro ainda não existe. O CRUD abaixo é só em memória.
 */

const DADOS = DADOS_SIMULADOS as unknown as DadosTabelaDePrecos;

const COLUNAS_ESQUELETO = 9;
const PADRAO_POR_PAGINA = 10;

function itemVazio(): ComponenteRemuneracao {
  return {
    id: '',
    nome: '',
    tipoPessoa: 'PF',
    vigenciaInicio: new Date().toISOString().slice(0, 10),
    vigenciaFim: null,
    versao: 1,
    perCapita: '0',
    fixoMensal: '0',
    cadeirante: '0',
    crechePerCapita: '0',
    especialFixo: '0',
    kmAdicional: '0',
    ativComplRegularPct: 0.025,
    ativComplCadeirantePct: 0.025,
    ccaPct: 0.5,
    alunosMinFixo: 20,
    ativa: true,
    observacoes: '',
  };
}

export interface ComponentesRemuneracaoTabHandle {
  abrirCriacao: () => void;
}

export const ComponentesRemuneracaoTab = forwardRef<ComponentesRemuneracaoTabHandle>(
  (_props, ref) => {
    const cenario = useCenarioDeTela();
    const [itens, setItens] = useState<ComponenteRemuneracao[]>([]);

    useEffect(() => {
      setItens(cenario === 'sucesso' ? DADOS.remuneracao : []);
    }, [cenario]);

    const [filtro, setFiltro] = useState<Record<string, string>>({});
    const [vigencia, setVigencia] = useState<DateRangeValue>({ preset: 'ALL' });
    const [pagina, setPagina] = useState(1);
    const [porPagina, setPorPagina] = useState(PADRAO_POR_PAGINA);
    const [modalAberto, setModalAberto] = useState(false);
    const [editando, setEditando] = useState<ComponenteRemuneracao | null>(null);
    const [form, setForm] = useState<ComponenteRemuneracao>(itemVazio());

    const abrirCriacao = () => {
      setEditando(null);
      setForm(itemVazio());
      setModalAberto(true);
    };

    const abrirEdicao = (item: ComponenteRemuneracao) => {
      setEditando(item);
      setForm(item);
      setModalAberto(true);
    };

    useImperativeHandle(ref, () => ({ abrirCriacao }));

    const salvar = () => {
      if (editando) {
        setItens((atual) =>
          atual.map((i) => (i.id === editando.id ? { ...form, id: editando.id } : i)),
        );
      } else {
        setItens((atual) => [...atual, { ...form, id: gerarIdLocal() }]);
      }
      setModalAberto(false);
    };

    const excluir = (id: string) => {
      setItens((atual) => atual.filter((i) => i.id !== id));
    };

    const filtrados = useMemo(() => {
      const busca = (filtro.busca ?? '').trim().toLowerCase();
      const { from, to } = resolveDateRange(vigencia);
      return itens.filter((item) => {
        if (busca && !item.nome.toLowerCase().includes(busca)) return false;
        if (filtro.tipo && item.tipoPessoa !== filtro.tipo) return false;
        if (filtro.status === 'ATIVA' && !item.ativa) return false;
        if (filtro.status === 'INATIVA' && item.ativa) return false;
        if (from && item.vigenciaInicio < from) return false;
        if (to && item.vigenciaInicio > to) return false;
        return true;
      });
    }, [itens, filtro, vigencia]);

    const temFiltro =
      !!filtro.busca || !!filtro.tipo || !!filtro.status || vigencia.preset !== 'ALL';

    const camposFiltro: FilterFieldConfig[] = [
      { key: 'busca', label: 'Buscar', type: 'search', placeholder: 'Buscar por nome', flex: true },
      {
        key: 'tipo',
        label: 'Tipo de pessoa',
        type: 'select',
        placeholder: 'Todos',
        width: 'w-full sm:w-[180px]',
        options: [
          { value: 'PF', label: 'Pessoa Física' },
          { value: 'PJ', label: 'Pessoa Jurídica' },
          { value: 'COOPERATIVA', label: 'Cooperativa' },
        ],
      },
      {
        key: 'status',
        label: 'Status',
        type: 'select',
        placeholder: 'Todos',
        width: 'w-full sm:w-[140px]',
        options: [
          { value: 'ATIVA', label: 'Ativa' },
          { value: 'INATIVA', label: 'Inativa' },
        ],
      },
    ];

    const totalPaginas = Math.max(1, Math.ceil(filtrados.length / porPagina));
    const paginaEfetiva = Math.min(pagina, totalPaginas);
    const paginados = filtrados.slice((paginaEfetiva - 1) * porPagina, paginaEfetiva * porPagina);

    const colunas: DataTableColumn<ComponenteRemuneracao>[] = [
      { header: 'Nome', accessorKey: 'nome', cellClassName: 'font-medium' },
      {
        header: 'Tipo',
        cell: (r) => <Badge variant="outline">{TIPO_PESSOA_LABELS[r.tipoPessoa]}</Badge>,
      },
      {
        header: 'Vigência',
        cell: (r) => (
          <TextoVigencia vigenciaInicio={r.vigenciaInicio} vigenciaFim={r.vigenciaFim} />
        ),
      },
      {
        header: 'Per capita',
        headerClassName: 'text-right',
        cellClassName: 'text-right',
        cell: (r) => <MoneyDisplay value={r.perCapita} />,
      },
      {
        header: 'Fixo mensal',
        headerClassName: 'text-right',
        cellClassName: 'text-right',
        cell: (r) => <MoneyDisplay value={r.fixoMensal} />,
      },
      {
        header: 'Cadeirante',
        headerClassName: 'text-right',
        cellClassName: 'text-right',
        cell: (r) => <MoneyDisplay value={r.cadeirante} />,
      },
      {
        header: 'Creche/PC',
        headerClassName: 'text-right',
        cellClassName: 'text-right',
        cell: (r) => <MoneyDisplay value={r.crechePerCapita} />,
      },
      {
        header: 'Especial fixo',
        headerClassName: 'text-right',
        cellClassName: 'text-right',
        cell: (r) => <MoneyDisplay value={r.especialFixo} />,
      },
      {
        header: 'KM adic.',
        headerClassName: 'text-right',
        cellClassName: 'text-right',
        cell: (r) => <MoneyDisplay value={r.kmAdicional} />,
      },
      {
        header: 'Ativa',
        cell: (r) => (
          <Badge variant={r.ativa ? 'default' : 'secondary'}>{r.ativa ? 'Sim' : 'Não'}</Badge>
        ),
      },
      {
        // Regra 10 de interface do CLAUDE.md: coluna de ações sempre por último —
        // o protótipo (`TabelaPrecosTab.tsx`) tinha "Ativa" depois de "Ações".
        header: 'Ações',
        headerClassName: 'w-[96px]',
        cell: (r) => (
          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => abrirEdicao(r)}
              aria-label={`Editar ${r.nome}`}
            >
              <Pencil className="h-4 w-4" />
            </Button>
            <ConfirmDialog
              trigger={
                <Button variant="ghost" size="icon" aria-label={`Excluir ${r.nome}`}>
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              }
              title="Excluir versão"
              description="Esta versão será removida da lista. Apurações já efetuadas não são afetadas."
              confirmLabel="Excluir"
              variant="destructive"
              onConfirm={() => excluir(r.id)}
            />
          </div>
        ),
      },
    ];

    return (
      <div className="space-y-4">
        <FilterBar
          fields={camposFiltro}
          values={filtro}
          onChange={(chave, valor) => {
            setFiltro((atual) => ({ ...atual, [chave]: valor }));
            setPagina(1);
          }}
          onClear={() => {
            setFiltro({});
            setVigencia({ preset: 'ALL' });
            setPagina(1);
          }}
          hasActiveFilters={temFiltro}
        >
          <DateRangePresetFilter
            label="Vigência início"
            value={vigencia}
            onChange={(proximo) => {
              setVigencia(proximo);
              setPagina(1);
            }}
          />
        </FilterBar>

        <EstadoSimulado
          cenario={cenario}
          descricao="os componentes da remuneração"
          colunasDoEsqueleto={COLUNAS_ESQUELETO}
        >
          <DataTable
            columns={colunas}
            data={paginados}
            total={filtrados.length}
            page={paginaEfetiva}
            perPage={porPagina}
            onPageChange={setPagina}
            onPerPageChange={(novo) => {
              setPorPagina(novo);
              setPagina(1);
            }}
            emptyIcon={DollarSign}
            emptyTitle={temFiltro ? 'Nenhum resultado' : 'Nenhuma versão cadastrada'}
            emptyDescription={
              temFiltro
                ? 'Ajuste os filtros para ver outros resultados.'
                : 'Cadastre a primeira versão dos componentes da remuneração.'
            }
            emptyActionLabel={temFiltro ? undefined : 'Nova versão'}
            onEmptyAction={temFiltro ? undefined : abrirCriacao}
            ariaLabel="Versões dos componentes da remuneração"
          />
        </EstadoSimulado>

        <ResponsiveModal
          open={modalAberto}
          onOpenChange={setModalAberto}
          title={editando ? 'Editar versão' : 'Nova versão'}
          desktopMaxWidth="max-w-2xl"
          footer={
            <>
              <Button variant="outline" onClick={() => setModalAberto(false)}>
                Cancelar
              </Button>
              <Button onClick={salvar}>Salvar</Button>
            </>
          }
        >
          <div className="grid gap-4">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="grid gap-2">
                <Label htmlFor="rem-nome">Nome</Label>
                <Input
                  id="rem-nome"
                  value={form.nome}
                  onChange={(e) => setForm({ ...form, nome: e.target.value })}
                  placeholder="Ex.: Tabela 2026 — PF"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="rem-tipo">Tipo de pessoa</Label>
                <Select
                  value={form.tipoPessoa}
                  onValueChange={(v) => setForm({ ...form, tipoPessoa: v as TipoPessoa })}
                >
                  <SelectTrigger id="rem-tipo">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PF">Pessoa Física</SelectItem>
                    <SelectItem value="PJ">Pessoa Jurídica</SelectItem>
                    <SelectItem value="COOPERATIVA">Cooperativa</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="grid gap-2">
                <Label htmlFor="rem-inicio">Vigência início</Label>
                <Input
                  id="rem-inicio"
                  type="date"
                  value={form.vigenciaInicio}
                  onChange={(e) => setForm({ ...form, vigenciaInicio: e.target.value })}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="rem-fim">Vigência fim</Label>
                <Input
                  id="rem-fim"
                  type="date"
                  value={form.vigenciaFim ?? ''}
                  onChange={(e) => setForm({ ...form, vigenciaFim: e.target.value || null })}
                />
              </div>
            </div>

            <div className="border-t border-border pt-4">
              <h3 className="mb-3 text-sm font-semibold">Componentes (R$)</h3>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div className="grid gap-2">
                  <Label htmlFor="rem-pc">Per capita</Label>
                  <MoneyInput
                    id="rem-pc"
                    value={form.perCapita}
                    onChange={(v) => setForm({ ...form, perCapita: v })}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="rem-fixo">Fixo mensal</Label>
                  <MoneyInput
                    id="rem-fixo"
                    value={form.fixoMensal}
                    onChange={(v) => setForm({ ...form, fixoMensal: v })}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="rem-cadeirante">Cadeirante</Label>
                  <MoneyInput
                    id="rem-cadeirante"
                    value={form.cadeirante}
                    onChange={(v) => setForm({ ...form, cadeirante: v })}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="rem-creche">Creche per capita</Label>
                  <MoneyInput
                    id="rem-creche"
                    value={form.crechePerCapita}
                    onChange={(v) => setForm({ ...form, crechePerCapita: v })}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="rem-especial">Especial fixo</Label>
                  <MoneyInput
                    id="rem-especial"
                    value={form.especialFixo}
                    onChange={(v) => setForm({ ...form, especialFixo: v })}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="rem-km">KM adicional (R$/km)</Label>
                  <MoneyInput
                    id="rem-km"
                    value={form.kmAdicional}
                    onChange={(v) => setForm({ ...form, kmAdicional: v })}
                  />
                </div>
              </div>
            </div>

            <div className="border-t border-border pt-4">
              <h3 className="mb-3 text-sm font-semibold">Percentuais</h3>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div className="grid gap-2">
                  <Label htmlFor="rem-acr">Ativ. compl. regular</Label>
                  <Input
                    id="rem-acr"
                    type="number"
                    step="0.001"
                    min="0"
                    value={form.ativComplRegularPct}
                    onChange={(e) =>
                      setForm({ ...form, ativComplRegularPct: Number(e.target.value) || 0 })
                    }
                  />
                  <span className="text-xs text-muted-foreground">
                    {formatarPercentual(form.ativComplRegularPct)}
                  </span>
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="rem-acc">Ativ. compl. cadeirante</Label>
                  <Input
                    id="rem-acc"
                    type="number"
                    step="0.001"
                    min="0"
                    value={form.ativComplCadeirantePct}
                    onChange={(e) =>
                      setForm({ ...form, ativComplCadeirantePct: Number(e.target.value) || 0 })
                    }
                  />
                  <span className="text-xs text-muted-foreground">
                    {formatarPercentual(form.ativComplCadeirantePct)}
                  </span>
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="rem-cca">CCA (% do per capita)</Label>
                  <Input
                    id="rem-cca"
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.ccaPct}
                    onChange={(e) => setForm({ ...form, ccaPct: Number(e.target.value) || 0 })}
                  />
                  <span className="text-xs text-muted-foreground">
                    {formatarPercentual(form.ccaPct)}
                  </span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 border-t border-border pt-4 sm:grid-cols-3">
              <div className="grid gap-2">
                <Label htmlFor="rem-alunos">Alunos mínimos p/ fixo</Label>
                <Input
                  id="rem-alunos"
                  type="number"
                  min="0"
                  value={form.alunosMinFixo}
                  onChange={(e) =>
                    setForm({ ...form, alunosMinFixo: parseInt(e.target.value, 10) || 0 })
                  }
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="rem-versao">Versão</Label>
                <Input
                  id="rem-versao"
                  type="number"
                  min="1"
                  value={form.versao}
                  onChange={(e) => setForm({ ...form, versao: parseInt(e.target.value, 10) || 1 })}
                />
              </div>
              <div className="flex items-center gap-3 pt-6">
                <Switch
                  checked={form.ativa}
                  onCheckedChange={(v) => setForm({ ...form, ativa: v })}
                  id="rem-ativa"
                />
                <Label htmlFor="rem-ativa">Ativa</Label>
              </div>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="rem-obs">Observações</Label>
              <Textarea
                id="rem-obs"
                rows={2}
                value={form.observacoes}
                onChange={(e) => setForm({ ...form, observacoes: e.target.value })}
              />
            </div>
          </div>
        </ResponsiveModal>
      </div>
    );
  },
);

ComponentesRemuneracaoTab.displayName = 'ComponentesRemuneracaoTab';
